import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import Input from '@superfunnel/ui-core/v2/Input';
import TextArea from '@superfunnel/ui-core/v2/TextArea';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface Props {
  initialName: string;
  initialDescription: string;
  initialPath?: string;
  /** Paths of other variants (for uniqueness validation) */
  siblingPaths?: string[];
  open: boolean;
  onClose: () => void;
  onSave: (
    name: string,
    description: string,
    path: string | null,
  ) => Promise<void>;
}

/**
 * Modal for editing variant name, description, and path in the AI Assistant draft state.
 * Unlike EditVariantNameAndDescriptionModal, this doesn't require DynamicPageContext
 * and saves via a callback instead of calling a service directly.
 */
export default function EditVariantDraftModal({
  initialName,
  initialDescription,
  initialPath,
  siblingPaths = [],
  open,
  onClose,
  onSave,
}: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [path, setPath] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    setName(initialName || '');
    setDescription(initialDescription || '');
    setPath(initialPath || '');
  }, [initialName, initialDescription, initialPath]);

  useEffect(() => {
    if (open) return;

    setSaving(false);
    setError(undefined);
  }, [open]);

  // Validate path: must be unique across variants, and cannot be just "v"
  const pathError = useMemo(() => {
    const trimmedPath = path.trim();
    if (!trimmedPath) return undefined;

    // Cannot be just "v" (reserved for variant path prefix)
    if (trimmedPath === 'v') {
      return 'Path cannot be just "v" (reserved)';
    }

    // Check uniqueness across sibling variants
    if (siblingPaths.includes(trimmedPath)) {
      return 'This path is already used by another variant';
    }

    // Basic path validation: no slashes, no special characters
    if (!/^[a-zA-Z0-9_-]+$/.test(trimmedPath)) {
      return 'Path can only contain letters, numbers, hyphens, and underscores';
    }

    return undefined;
  }, [path, siblingPaths]);

  const handleSave = useCallback(async () => {
    if (saving) return;
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (pathError) return;

    setSaving(true);
    setError(undefined);

    try {
      const trimmedPath = path.trim();
      await onSave(name.trim(), description.trim(), trimmedPath || null);
    } catch (e: unknown) {
      console.error('[EditVariantDraftModal] Error saving:', e);
      setError(e instanceof Error ? e.message : 'Failed to save changes');
      setSaving(false);
    }
  }, [saving, name, description, path, pathError, onSave]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: 'Update',
        type: 'primary',
        loading: saving,
        disabled: !!pathError,
        onClick: () => handleSave(),
      },
    ],
    [saving, pathError, onClose, handleSave],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      width="lg"
      title="Update variant details"
    >
      <div className="flex flex-col gap-4">
        <Input
          title="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter variant name"
        />
        <TextArea
          title="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter variant description (optional)"
        />
        <div>
          <Input
            title="Path (optional)"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="e.g., mobile-users"
          />
          {pathError && (
            <p className="mt-1 text-sm text-red-600">{pathError}</p>
          )}
          {!path.trim() && (
            <p className="mt-1 text-sm text-gray-400">
              Add a sub-path to access this variant directly via URL
            </p>
          )}
        </div>
      </div>
      {error && (
        <div className="mt-5 w-full text-end text-red-600">{error}</div>
      )}
    </Modal>
  );
}
